import { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import {
  GoabPageBlock,
  GoabModal
} from "@abgov/react-components";
import { DynamicFields } from "./DynamicFields";
import { validateInput, scrollToError } from "../../../common/form/formValidation";
import { useDynamicForm } from "../hooks/useDynamicForm";
import { tryParse, viewOnlyApplication, namePattern, emailPattern, phonePattern, evaluate } from "../../../utils/globalUtility";
import { constants } from "../../../common/constants/statuses";
import { STEP_TYPES, SUBMISSION_TYPE } from "../../../common/constants/types";
import { useUnsavedChanges } from "../../../common/unsavedChangesContext";

const DynamicFormWrapper = (input) => {
  const {
    stepData,
    currentStep,
    dynamicForm,
    portal,
    getResourceValue, 
    getRefData, 
    clickHandlers, 
    applicationType, 
    internalStatus, 
    onNext = null, 
    saveStepData = null, 
    moveToReviewStep, 
    moveToStep, 
    submitApplication, 
    withdrawApplication, 
    getStepData, 
    user, 
    isLoggedIn, 
    inReviewMode, 
    userApplicationData, 
    userDataUpdated, 
    setUserDataUpdated, 
    activeEvents = [], 
    availableEvents = [], 
    selectedEvent, 
    isActivePortal, 
    isEventsEnabled, 
    isActiveEvent, 
    programEvents = [],
    programFeatures = {},
    StepActionButtons = null,
    onProxySaveSuccess = null,
    supportInfo = "",
    statusTransitionEvent,
    verificationStatusChangeEvent,
    followUpInstruction,
    userDataChanged,
  } = input;

  const [showStepActionButtons, setShowStepActionButtons] = useState(true);
  const [nextButtonClicked, setNextButtonClicked] = useState(false);

  const currentStepType = currentStep?.renderer?.props?.type;
  const isApplicationStep = currentStep?.renderer?.props?.isApplicationStep;
  const form = tryParse(dynamicForm?.template);

  const [modalsState, setModalsState] = useState(
    Object.keys(form?.modals ?? {}).reduce((state, key) => {
      state[key] = false;
      return state;
    }, {})
  );

  // Utility function to toggle modal visibility
  const toggleModal = (modalKey, isVisible) => {
    setModalsState((prevState) => ({
      ...prevState,
      [modalKey]: isVisible,
    }));
  };

  clickHandlers['toggleModal']=toggleModal;
  clickHandlers['moveToStep']=moveToStep;
  clickHandlers['withdrawApplication']=withdrawApplication;

  const {
    defaultValues,
    title,
    description,
    formSettings,
    error,
    isLoading,
    fields,
  } = useDynamicForm({
    form,
    context: { form: stepData || {}, user }
  });

  const { hasUnsavedChanges, addDirty, removeDirty, clearDirty } = useUnsavedChanges();

  const isEmpty = (obj) => Object.keys(context.InternalStatus)?.length == 0;

  const canEditApplication = !viewOnlyApplication(internalStatus, user);

  let realUser = user;
  if (user.isProxyUser && !!userApplicationData.SubmissionType && userApplicationData.SubmissionType !== SUBMISSION_TYPE.Proxy) {
    realUser = {...user, isProxyUser: false, verificationStatus: userApplicationData.userVerificationStatus,
      givenName: userApplicationData.givenName, surname: userApplicationData.surname, middleName: userApplicationData.middleName,
      email: userApplicationData.email, dob: userApplicationData.dob, gender: userApplicationData.gender, country: userApplicationData.country,
      streetAddressLine1: userApplicationData.streetAddressLine1, streetAddressLine2: userApplicationData.streetAddressLine2,
      locality: userApplicationData.locality, province: userApplicationData.province, postalCode: userApplicationData.postalCode,
      phone: userApplicationData.phone, phoneExtension: userApplicationData.phoneExtension, mobile: userApplicationData.mobile
    };
  }

  const context = {
    stepData,
    getStepData,
    user: realUser,
    getRefData,
    internalStatus: internalStatus,
    statusTransitionEvent: statusTransitionEvent,
    verificationStatusChangeEvent: verificationStatusChangeEvent,
    applicationType: applicationType,
    eventType: selectedEvent,
    programEvents: activeEvents,
    availableEvents: availableEvents,
    isActivePortal,
    isActiveEvent,
    isEventsEnabled,
    canEditApplication,
    programStatus: portal.programStatus,
    templateVersion: portal.template?.version,
    isEmpty,
    hasUnsavedChanges: hasUnsavedChanges,
    followUpInstruction: followUpInstruction,
    patternValidations: {namePattern, emailPattern, phonePattern},
    userDataChanged: userDataChanged,
    userApplicationData: userApplicationData,
    portal: portal,
    supportInfo: supportInfo,
    programFeatures: programFeatures,
    inReviewMode,
    ...constants
  };

  const handleStepSave = async (values) => {
    const dataUpdated = await saveStepData({ ...values }, fields, context, getRefData);

    if (dataUpdated) {
      setUserDataUpdated?.(dataUpdated);
    } else {
      if (!userDataUpdated) removeDirty(currentStep?.name);
    }

    // Only redirect proxy users to internal portal if the application has been
    // submitted/saved to the API (internalStatus is set and not Draft).
    // For new or draft applications, return to the Review step instead.
    const isDraftOrNew = !internalStatus || internalStatus === context.InternalStatus.Draft;
    if (context.user.isProxyUser && !isDraftOrNew) {
      clearDirty();
      if(dataUpdated){
        onProxySaveSuccess?.();
      }
    } else {
      moveToReviewStep();
    }
  };

  const templateForm = useFormik({
    initialValues: defaultValues,
    enableReinitialize: true,
    validate: async (values) => {
      const errors = await validateInput({ form, data: values, context });
      if(nextButtonClicked && errors){
        scrollToError(form, values, context, errors);
        setNextButtonClicked(false);
      }
      return errors;
    },
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (!inReviewMode) {
        await onNext?.({ ...values }, fields, context, getRefData);
        return;
      }
      if (currentStepType === STEP_TYPES.Review) {
        await submitApplication(null, values, fields, context, getRefData);
      } else if (saveStepData) {
        await handleStepSave(values);
      }
    }
  });

  useEffect(() => {
    if(formSettings?.skipRules){
      const skip = formSettings.skipRules.find(rule => {
        const shouldSkip = evaluate(rule, context, form);
        return shouldSkip;
      })

      if(skip){
        onNext({...skip.defaultValues, isInternal: true});
        return;
      }
    }

    if(formSettings?.moveToStepRules){
      const step = formSettings.moveToStepRules.find(rule => {
        const shouldMoveToStep = evaluate(rule, context, form);
        return shouldMoveToStep;
      });

      if (step) {
        moveToStep(step.stepName);
        return;
      }
    }

    if(formSettings?.focusRules){
      const focusedElement = formSettings.focusRules.find(rule => {
        const shouldFocus = evaluate(rule, context, form);
        return shouldFocus && document.getElementById(rule.id);
      });

      if (focusedElement) {
        const element = document.getElementById(focusedElement.id);
        element?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [formSettings, context]);

  const initialSnapshot = useRef(null);
  if (initialSnapshot.current === null) {
    initialSnapshot.current = JSON.stringify(defaultValues);
  }

  useEffect(() => {
    // Only track unsaved changes for steps that require login (actual data steps), and step data or in review
    if (isLoggedIn && currentStep?.renderer?.props?.requiresLogin && (isApplicationStep || currentStepType === STEP_TYPES.Review)) {
      const hasRealChanges = JSON.stringify(templateForm.values) !== initialSnapshot.current;
      if (hasRealChanges) {
        addDirty(currentStep?.name);
      } else {
        removeDirty(currentStep?.name);
      }
    }
  }, [templateForm.values, isLoggedIn, currentStep]);

  clickHandlers['toggleModal']=toggleModal;
  clickHandlers['moveToStep']=moveToStep;
  clickHandlers['withdrawApplication']=withdrawApplication;

  const handleNextButtonSubmit = () => { 
    setNextButtonClicked(true); 
    setTimeout(() => templateForm.handleSubmit()); 
  };

  clickHandlers['submitApplication'] = handleNextButtonSubmit; 
    
  return ( 
    <GoabPageBlock width="704px"> 

      <DynamicFields 
        children={form?.fields} 
        form={templateForm} 
        getResourceValue={getResourceValue} 
        getRefData={getRefData} 
        clickHandlers={clickHandlers} 
        context={context} 
        setShowStepActionButtons={setShowStepActionButtons}
        supportInfo={supportInfo}
        /> 

          {Object.entries(form?.modals ?? {}).map(([key, modalConfig]) => ( 
            <GoabModal 
              key={key} 
              calloutVariant={modalConfig.properties.calloutVariant} 
              heading={getResourceValue({ 
                key: 'RESOURCES@' + modalConfig.properties.heading 
              })} 
              open={modalsState[key]} 
              role={modalConfig.properties.role || "dialog"} 
              > 
                <DynamicFields 
                  children={modalConfig?.fields}
                  getResourceValue={getResourceValue} 
                  getRefData={getRefData} 
                  clickHandlers={clickHandlers} 
                  context={context} 
                  supportInfo={supportInfo}
                  /> 
              </GoabModal> 
              ))} 
          
          {showStepActionButtons && ( 
            <StepActionButtons 
              {...input} 
              templateForm={templateForm} 
              fields={fields} 
              context={context} 
              handleSubmit={handleNextButtonSubmit} 
              getFormValues={() => templateForm.values} 
              isLoading={isLoggedIn} 
              />)} 
    </GoabPageBlock> 
  ); 
}; 

const DynamicFormRenderer = (props) => { 
  return <DynamicFormWrapper {...props} />; 
}; 

export default DynamicFormRenderer;
  